import { useEffect, useState } from 'react';
import { CommandeAnalyse } from '../domain/analyse/types';

// Document du bloc « Rappel des documents associés » (forme attendue par DocumentsList).
export interface MarcheDocument {
  id: number;
  type: string;
  updated_at: string;
  key?: string;
  category?: 'entreprise' | 'accord_cadre' | 'commun';
  filename?: string;
  displayLabel?: string;
}

// Forme brute renvoyee par les endpoints /documents/* (sous-ensemble utile, jamais `any`).
interface ApiDoc {
  id: number;
  type?: string;
  updated_at?: string;
  key?: string;
  filename?: string;
  actif?: boolean;
}

const API = process.env.REACT_APP_API_URL;
const authHeaders = (): HeadersInit => ({
  Authorization: `Bearer ${localStorage.getItem('token')}`,
  'Content-Type': 'application/json',
});

// Les 2 CCTP affiches (documents communs), dans cet ordre.
const CCTP_TYPES = ['cctp-ingenierie', 'cctp-travaux'];

const toDoc = (d: ApiDoc, extra: Partial<MarcheDocument>): MarcheDocument => ({
  id: d.id,
  type: d.type ?? '',
  updated_at: d.updated_at ?? '',
  key: d.key,
  filename: d.filename,
  ...extra,
});

// Documents affiches sous le PDF : le Contrat (accord-cadre du titulaire) puis les 2 CCTP
// (documents communs : ingenierie, travaux), dans cet ordre. Le RIB et les autres documents du
// titulaire sont accessibles via la popup profil (bouton « Voir les autres documents... »).
// Memes endpoints que Coordonnees ; DocumentsList gere preview + telechargement.
export const useMarcheDocuments = (
  retenu?: CommandeAnalyse,
): { documents: MarcheDocument[]; loading: boolean } => {
  const [documents, setDocuments] = useState<MarcheDocument[]>([]);
  const [loading, setLoading] = useState(false);

  const contratId = retenu?.contrat?.id;
  const contratNumero = retenu?.contrat?.numero ?? undefined;
  // Lot du contrat de la commande retenue (onglet verrouille sur le retenu, pas l'operation).
  const contratLot = retenu?.contrat?.id_lot_technique;

  useEffect(() => {
    if (!contratId) {
      setDocuments([]);
      return undefined;
    }
    let cancelled = false;

    const getJson = async <T>(path: string): Promise<T | null> => {
      try {
        const res = await fetch(`${API}${path}`, { headers: authHeaders() });
        return res.ok ? ((await res.json()) as T) : null;
      } catch {
        return null;
      }
    };

    const load = async (): Promise<void> => {
      setLoading(true);
      const collected: MarcheDocument[] = [];

      // 1. Contrat (document accord-cadre du contrat du titulaire) — en premier.
      const accordCadre = await getJson<{ data?: ApiDoc[] }>(`/documents/accord-cadre/${contratId}`);
      const contrat = (accordCadre?.data ?? []).find((d) => d.type?.toLowerCase().startsWith('contrat'));
      if (contrat) {
        collected.push(
          toDoc(contrat, {
            category: 'accord_cadre',
            displayLabel: contratNumero ? `CCAP + AE (lot ${contratLot ?? ''}) : Contrat N°${contratNumero}` : undefined,
          }),
        );
      }

      // 2. CCTP ingenierie puis travaux (documents communs), dans l'ordre CCTP_TYPES.
      const communs = await getJson<{ data?: { data?: ApiDoc[] } }>('/documents-communs?per_page=100');
      const cctpDocs = (communs?.data?.data ?? []).filter(
        (d) => d.actif && d.type !== undefined && CCTP_TYPES.includes(d.type),
      );
      CCTP_TYPES.forEach((t) => {
        const found = cctpDocs.find((d) => d.type === t);
        if (found) collected.push(toDoc(found, { category: 'commun' }));
      });

      if (!cancelled) {
        setDocuments(collected);
        setLoading(false);
      }
    };

    load();
    return () => {
      cancelled = true;
    };
  }, [contratId, contratNumero, contratLot]);

  return { documents, loading };
};
